import React, { useState, useEffect } from "react";
import { supabase } from '../../lib/supabaseClient';
import ballIcon from '../../assets/messages/ball.png';

const FRONTEND_FALLBACK_LOGO_ASSET = ballIcon;
const BACKEND_DEFAULT_TEAM_LOGO_PATH = '/uploads/teams/7b9a405a6a953b17e04a8f0107c3e5fd.jpg';

// Cache for storing promises of logo downloads. This is shared across all instances of TeamLogo.
const logoPromiseCache = new Map();

// Helper function to clean up and validate logo URLs
const getCleanLogoUrl = (logoUrl) => {
    if (!logoUrl || logoUrl.trim() === "" || logoUrl === "/uploads/teams/default-team-logo.png") {
        return BACKEND_DEFAULT_TEAM_LOGO_PATH;
    }
    if (logoUrl === FRONTEND_FALLBACK_LOGO_ASSET) {
        return logoUrl;
    }
    if (logoUrl.startsWith('/https://') || logoUrl.startsWith('/http://')) {
        return logoUrl.substring(1);
    }
    if (logoUrl.startsWith('https://') || logoUrl.startsWith('http://')) {
        return logoUrl;
    }
    if (logoUrl.startsWith('/uploads/')) {
        return logoUrl;
    }
    console.warn(`[getCleanLogoUrl] Unrecognized logo URL format: "${logoUrl}". Falling back to backend default.`);
    return BACKEND_DEFAULT_TEAM_LOGO_PATH;
};

// Helper function to extract file path from Supabase URL
const extractSupabaseFilePath = (url) => {
    if (!url || !url.includes('supabase.co')) return null;
    const match = url.match(/\/storage\/v1\/object\/public\/(.*)/);
    return match ? match[1] : null;
};

// Component for handling individual team logo with a shared promise-based cache
export const TeamLogo = ({ logoUrl, teamName, className = "" }) => {
    const [currentSrc, setCurrentSrc] = useState(FRONTEND_FALLBACK_LOGO_ASSET);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        let isMounted = true;

        const loadLogo = async () => {
            if (!isMounted) return;

            if (!logoUrl) {
                setCurrentSrc(FRONTEND_FALLBACK_LOGO_ASSET);
                setIsLoading(false);
                return;
            }

            const cleanUrl = getCleanLogoUrl(logoUrl);

            if (!cleanUrl.includes('supabase.co')) {
                setCurrentSrc(cleanUrl);
                setIsLoading(false);
                return;
            }
            
            const filePath = extractSupabaseFilePath(cleanUrl);
            if (!filePath) {
                console.warn(`Could not extract file path from Supabase URL: ${cleanUrl}`);
                setCurrentSrc(FRONTEND_FALLBACK_LOGO_ASSET);
                setIsLoading(false);
                return;
            }

            if (!logoPromiseCache.has(filePath)) {
                const downloadPromise = supabase.storage
                    .from(filePath.split('/')[0])
                    .download(filePath.split('/').slice(1).join('/'))
                    .then(({ data, error }) => {
                        if (error) throw error;
                        return URL.createObjectURL(data);
                    })
                    .catch(error => {
                        console.error(`Error loading ${teamName} logo from Supabase (${filePath}):`, error.message);
                        logoPromiseCache.delete(filePath);
                        throw error;
                    });
                
                logoPromiseCache.set(filePath, downloadPromise);
            }

            try {
                setIsLoading(true);
                const blobUrl = await logoPromiseCache.get(filePath);
                if (isMounted) {
                    setCurrentSrc(blobUrl);
                }
            } catch (error) {
                if (isMounted) {
                    setCurrentSrc(FRONTEND_FALLBACK_LOGO_ASSET);
                }
            } finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        };

        loadLogo();
        
        return () => {
            isMounted = false;
        };
    }, [logoUrl, teamName]);

    const handleError = () => {
        if (currentSrc !== FRONTEND_FALLBACK_LOGO_ASSET) {
            setCurrentSrc(FRONTEND_FALLBACK_LOGO_ASSET);
        }
    };

    if (isLoading) {
        return <img src={FRONTEND_FALLBACK_LOGO_ASSET} alt={`Loading ${teamName || 'team'} logo...`} className={`${className} loading-opacity`} style={{ opacity: 0.5 }} />;
    }

    return <img src={currentSrc} alt={teamName || 'Team logo'} className={className} onError={handleError} />;
}; 